import React, { useState, useEffect, Suspense, lazy, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BACKEND_URL } from './services/config';
import { checkAndRefreshToken, isTokenExpired } from './services/auth';
import Header from './components/Header';
import PreActivation from './components/PreActivation';
import WhatsAppFloat from './components/WhatsAppFloat';
import BottomNav from './components/BottomNav';
import MarketingAngle from './components/MarketingAngle';
import AuthPage from './components/AuthPage';
import { ToastProvider } from './components/Toast';
import { ListSkeleton, StatsSkeleton } from './components/Skeleton';
import { LoadingWithTimeout } from './components/LoadingStates';
import LiveSupport from './components/LiveSupport';
import SystemGuideModal from './components/SystemGuideModal';
import LivePriceTicker from './components/LivePriceTicker';
import MarketSessionBanner from './components/MarketSessionBanner';
import { getUserStatus, activateUser, getExchangeRate, getTradingAccounts, trackPageVisit, getGuideProgress, saveGuideProgress, getUserPreferences, updateUserPreferences } from './services/api';
import { LayoutDashboard, Calculator, BookOpen, TrendingUp, Brain, Settings, HelpCircle, GraduationCap, MessageCircle, Menu, X, CheckSquare, Trophy, BarChart3, Flame, FlaskConical, Workflow, Globe, Sun, Moon, Target, Crown, LogOut, Wallet, RefreshCw, User } from 'lucide-react';

// v7.6: Lazy load ALL tab components for faster initial load
// Only loads when user switches to that tab
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const PremiumFeatures = lazy(() => import('./components/PremiumFeatures'));
const RiskCalculator = lazy(() => import('./components/RiskCalculator'));
const MarketSnapshot = lazy(() => import('./components/MarketSnapshot'));
const TradingJournal = lazy(() => import('./components/TradingJournal'));
const DrawdownAlert = lazy(() => import('./components/DrawdownAlert'));
const CompoundingCalculator = lazy(() => import('./components/CompoundingCalculator'));
const BreakEvenCalculator = lazy(() => import('./components/BreakEvenCalculator'));
const EconomicCalendar = lazy(() => import('./components/EconomicCalendar'));
const WeeklyReport = lazy(() => import('./components/WeeklyReport'));
const AIChat = lazy(() => import('./components/AIChat'));
const DashboardSummary = lazy(() => import('./components/DashboardSummary'));
const TargetTracker = lazy(() => import('./components/TargetTracker'));
const AccountManager = lazy(() => import('./components/AccountManager'));
const ExportData = lazy(() => import('./components/ExportData'));
const MT5Integration = lazy(() => import('./components/MT5Integration'));
const FeatureGuide = lazy(() => import('./components/FeatureGuide'));
const TradingEducation = lazy(() => import('./components/TradingEducation'));
const PipValueCalculator = lazy(() => import('./components/PipValueCalculator'));
const DailyChecklist = lazy(() => import('./components/DailyChecklist'));
const SessionAnalysis = lazy(() => import('./components/SessionAnalysis'));
const EmotionAnalysis = lazy(() => import('./components/EmotionAnalysis'));
const Achievements = lazy(() => import('./components/Achievements'));
const RiskHeatMap = lazy(() => import('./components/RiskHeatMap'));
const StrategyBuilder = lazy(() => import('./components/StrategyBuilder'));
const WalletDashboard = lazy(() => import('./components/WalletDashboard'));
const EquityCurve = lazy(() => import('./components/EquityCurve'));

// Loading fallback component for lazy loaded components
const LazyLoadFallback = () => (
  <LoadingWithTimeout 
    isLoading={true} 
    loadingText="Loading module..."
    slowText="Module is loading, please wait..."
  >
    <div />
  </LoadingWithTimeout>
);

import ErrorBoundary from './components/ErrorBoundary';

// v10.2: Redesign components
import HomeScreen from './components/redesign/HomeScreen';
import BottomNavNew from './components/redesign/BottomNavNew';
import TradeEngine from './components/redesign/TradeEngine';
import PerformanceDashboard from './components/redesign/PerformanceDashboard';
import LearnHub from './components/redesign/LearnHub';
import ProfileScreen from './components/redesign/ProfileScreen';
import InstallPrompt from './components/InstallPrompt';

// v7.6: Lightweight tab loading indicator
const TabFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', opacity: 0.6 }}>
    <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-subtle)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

function AppContent() {
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const [isActivated, setIsActivated] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({ usd_to_sgd: 1.34, usd_to_php: 56.50 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const validTabs = ['home', 'trade', 'learn', 'performance', 'profile', 'guide', 'checklist', 'tools', 'premium'];
    
    // Check URL query param first (e.g. ?tab=premium from Telegram)
    const urlParams = new URLSearchParams(window.location.search);
    const urlTab = urlParams.get('tab');
    if (urlTab && validTabs.includes(urlTab)) {
      return urlTab;
    }
    
    const savedTab = localStorage.getItem('fira-active-tab');
    if (savedTab) {
      localStorage.removeItem('fira-active-tab');
      if (validTabs.includes(savedTab)) {
        return savedTab;
      }
    }
    return 'home';
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isLightTheme = false; // Dark mode only
  const { t, language } = useLanguage();
  
  // Admin panel state - 7 clicks on logo to access (whitelist protected)
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [adminWhitelistChecked, setAdminWhitelistChecked] = useState(false);
  
  // Page tracking for BI
  const [pageStartTime, setPageStartTime] = useState(Date.now());

  // Guide completion state - check localStorage first, then sync with backend
  const [guideComplete, setGuideComplete] = useState(() => {
    return localStorage.getItem('fira-quiz-complete') === 'true';
  });
  const [showGuideReminder, setShowGuideReminder] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  
  // Refresh key - increments when tab changes to force data refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Force dark mode always
  useEffect(() => {
    document.documentElement.classList.add('dark-mode');
  }, []);

  // Fetch user preferences (theme, language) from backend on login
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchUserPreferences = async () => {
      try {
        const result = await getUserPreferences();
        if (result.success && result.preferences) {
          const prefs = result.preferences;
          // Apply theme from backend if different from localStorage
          if (prefs.theme) {
            const backendIsLight = prefs.theme === 'light';
            const localIsLight = localStorage.getItem('fira-theme') === 'light';
            
            // Backend takes precedence - sync to local
            if (backendIsLight !== localIsLight) {
              console.log('[THEME] Syncing theme from backend:', prefs.theme);
              setIsLightTheme(backendIsLight);
              localStorage.setItem('fira-theme', prefs.theme);
            }
          }
        }
      } catch (error) {
        console.log('[PREFS] Failed to fetch user preferences:', error.message);
      }
    };
    
    fetchUserPreferences();
  }, [isAuthenticated]);

  // Fetch guide progress from backend when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchGuideProgress = async () => {
      try {
        const progress = await getGuideProgress();
        if (progress.quiz_complete) {
          setGuideComplete(true);
          localStorage.setItem('fira-quiz-complete', 'true');
        }
      } catch (error) {
        console.error('Error fetching guide progress:', error);
      }
    };
    
    fetchGuideProgress();
  }, [isAuthenticated]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Increment refreshKey when tab changes to force data refresh
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [activeTab]);

  // Listen for LiveSupport tab navigation events
  useEffect(() => {
    const handleNavigateToTab = (e) => {
      setActiveTab(e.detail);
      setSidebarOpen(false);
    };
    
    window.addEventListener('navigateToTab', handleNavigateToTab);
    
    return () => {
      window.removeEventListener('navigateToTab', handleNavigateToTab);
    };
  }, []);

  // Token refresh on app resume/visibility change
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[APP] App became visible, checking token...');
        
        // Check if token is expired
        if (isTokenExpired()) {
          console.log('[APP] Token expired, logging out...');
          logout();
          return;
        }
        
        // Try to refresh token if expiring soon
        const tokenValid = await checkAndRefreshToken();
        if (!tokenValid) {
          console.log('[APP] Token refresh failed, logging out...');
          logout();
        }
      }
    };
    
    // Check token on mount
    handleVisibilityChange();
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check periodically (every 5 minutes)
    const intervalId = setInterval(handleVisibilityChange, 5 * 60 * 1000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [isAuthenticated, logout]);

  useEffect(() => {
    // Only fetch data if authenticated
    if (!isAuthenticated) return;
    
    const fetchInitialData = async () => {
      // Quick start - show app immediately, then load data
      setLoading(false);
      setIsActivated(true);
      setExchangeRates({ usd_to_sgd: 1.34, usd_to_php: 56.50 });
      
      // Load ALL data in parallel for faster startup
      try {
        const [userStatus, rateData, accounts] = await Promise.all([
          getUserStatus().catch(() => ({ is_activated: true })),
          getExchangeRate().catch(() => ({ usd_to_sgd: 1.34, usd_to_php: 56.50 })),
          getTradingAccounts().catch(() => [])
        ]);
        
        setIsActivated(userStatus.is_activated);
        setExchangeRates({ usd_to_sgd: rateData.usd_to_sgd, usd_to_php: rateData.usd_to_php });
        setTradingAccounts(accounts);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, [isAuthenticated]);

  // Redirect to login if not authenticated (MUST be after all hooks)
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Show loading while checking auth
  if (authLoading) {
    return <LazyLoadFallback />;
  }

  const handleActivate = async () => {
    try {
      await activateUser('DEMO-123456');
      setIsActivated(true);
    } catch (error) {
      console.error('Error activating user:', error);
      setIsActivated(true);
    }
  };

  const handleGuideComplete = async () => {
    setGuideComplete(true);
    localStorage.setItem('fira-quiz-complete', 'true');
    
    // Save to backend so it persists across devices/sessions
    try {
      await saveGuideProgress({
        completed_sections: JSON.parse(localStorage.getItem('fira-guide-progress') || '[]'),
        quiz_complete: true
      });
    } catch (error) {
      console.error('Error saving guide progress:', error);
    }
  };

  const handleTabChange = (tabId) => {
    // Valid tabs that have rendering in App.js
    const validTabs = ['home', 'trade', 'learn', 'performance', 'profile', 'guide', 'checklist', 'tools', 'premium'];
    
    if (!validTabs.includes(tabId)) {
      tabId = 'home';
    }
    
    // Track page visit duration for BI
    const duration = Math.floor((Date.now() - pageStartTime) / 1000);
    if (activeTab && duration > 0) {
      trackPageVisit(activeTab, duration);
    }
    setPageStartTime(Date.now());
    
    // If guide not complete and trying to navigate away from guide, show reminder
    if (!guideComplete && tabId !== 'guide' && activeTab === 'guide') {
      setShowGuideReminder(true);
      setPendingTab(tabId);
      return;
    }
    setActiveTab(tabId);
    setSidebarOpen(false);
  };
  
  // Handle logo click for hidden admin panel (whitelist protected)
  const handleLogoClick = async () => {
    const now = Date.now();
    // Reset count if more than 2 seconds since last click
    if (now - lastClickTime > 2000) {
      setLogoClickCount(1);
    } else {
      setLogoClickCount(prev => prev + 1);
    }
    setLastClickTime(now);
    
    // Open admin panel after 7 clicks - but only if whitelisted
    if (logoClickCount >= 4) {
      setLogoClickCount(0);
      
      // Check whitelist silently - no error shown if not allowed
      try {
        const token = localStorage.getItem('fira_auth_token');
        if (!token) return; // Silent fail - not logged in
        
        const API_URL = BACKEND_URL;
        const response = await fetch(`${API_URL}/api/admin/whitelist/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.allowed) {
          setShowAdminPanel(true);
          setAdminWhitelistChecked(true);
        }
        // Silent fail if not allowed - nothing happens
      } catch (e) {
        // Silent fail - no error shown
        console.log('[Admin] Access check failed');
      }
    }
  };

  const handleSkipGuide = () => {
    setShowGuideReminder(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setSidebarOpen(false);
  };

  // Skip guide permanently - mark as complete and don't show reminder again
  const handleSkipGuidePermanently = async () => {
    setShowGuideReminder(false);
    setGuideComplete(true);
    localStorage.setItem('fira-quiz-complete', 'true');
    
    // Save to backend
    try {
      await saveGuideProgress({
        completed_sections: ['skipped'],
        skipped: true,
        skipped_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving guide skip:', error);
    }
    
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setSidebarOpen(false);
  };

  const handleBackToGuide = () => {
    setShowGuideReminder(false);
    setPendingTab(null);
  };

  // If auth is still loading, show loading spinner
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  if (loading) {
    return (
      <div className="app-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', padding: '20px', width: '100%', maxWidth: '400px' }}>
          <div style={{ 
            width: '50px', 
            height: '50px', 
            border: '3px solid var(--border-subtle)', 
            borderTop: '3px solid #F68B2D', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite', 
            margin: '0 auto 20px' 
          }} />
          <div style={{ color: '#F68B2D', fontWeight: '600', marginBottom: '20px' }}>Loading MBE Signal AI...</div>
          <StatsSkeleton />
        </div>
      </div>
    );
  }
  
  // Show Admin Panel if activated (with lazy loading)
  if (showAdminPanel) {
    return (
      <Suspense fallback={<LazyLoadFallback />}>
        <AdminPanel onBack={() => setShowAdminPanel(false)} />
      </Suspense>
    );
  }

  if (!isActivated) {
    return (
      <div className="app-container">
        <PreActivation onActivate={handleActivate} />
        {/* WhatsApp Float removed - no function */}
      </div>
    );
  }

  const tabs = [
    { id: 'home', label: 'Home', icon: LayoutDashboard },
    { id: 'trade', label: 'Trade', icon: TrendingUp, highlight: true },
    { id: 'learn', label: 'Learn', icon: GraduationCap },
    { id: 'performance', label: 'Stats', icon: BarChart3 },
    { id: 'profile', label: 'Profile', icon: User },
  ];

  return (
    <div className="app-container" style={{ display: 'flex' }}>
      {/* Desktop Sidebar - Always Visible */}
      {!isMobile && (
        <div 
          className="desktop-sidebar"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '260px',
            height: '100vh',
            background: '#FFFFFF',
            borderRight: '1px solid #F1F5F9',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Sidebar Header */}
          <div style={{
            padding: '16px 18px',
            borderBottom: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
          }}
          onClick={handleLogoClick}
          data-testid="logo-click-area"
          >
            <img
              src="/mbe-logo.jpeg"
              alt="MBE Logo"
              style={{ width: '38px', height: '38px', borderRadius: '10px', border: '2px solid #F68B2D' }}
            />
            <div>
              <div style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>MBE Signal AI</div>
              <div style={{ fontSize: '10px', color: '#94A3B8' }}>Trade Engine</div>
            </div>
          </div>

          {/* Menu Items */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const isSignalTab = tab.id === 'trade';
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  data-testid={`menu-${tab.id}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isSignalTab ? '14px 14px' : '12px 14px',
                    marginBottom: isSignalTab ? '6px' : '3px',
                    marginTop: isSignalTab ? '6px' : '0',
                    background: isSignalTab 
                      ? 'linear-gradient(135deg, #F5A623 0%, #E8951A 100%)'
                      : isActive 
                        ? '#FFF7ED'
                        : 'transparent',
                    border: 'none',
                    borderRadius: isSignalTab ? '12px' : '10px',
                    cursor: 'pointer',
                    color: isSignalTab ? '#FFFFFF' : isActive ? '#F5A623' : '#64748B',
                    fontSize: '14px',
                    fontWeight: isSignalTab ? '700' : isActive ? '600' : '500',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    boxShadow: isSignalTab 
                      ? '0 4px 15px rgba(245, 166, 35, 0.3)' 
                      : 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {isSignalTab && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: 'premiumShimmer 2s infinite'
                    }} />
                  )}
                  <tab.icon size={18} style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    filter: isSignalTab ? 'drop-shadow(0 0 3px rgba(255,255,255,0.5))' : 'none'
                  }} />
                  <span style={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    {tab.label || t(tab.labelKey)}
                  </span>
                  {tab.badge && (
                    <span style={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '9px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1,
                      color: '#fff',
                      textTransform: 'uppercase'
                    }}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div style={{
            padding: '14px 16px',
            borderTop: '1px solid #F1F5F9',
          }}>
            {/* User Info */}
            {user && (
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: '#0F172A', fontWeight: '500' }}>
                  {user.name}
                </div>
                <div style={{ fontSize: '11px', color: '#94A3B8' }}>
                  {user.email}
                </div>
              </div>
            )}
            {/* Logout Button */}
            <button
              onClick={logout}
              data-testid="logout-btn-desktop"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '8px'
              }}
            >
              <LogOut size={16} />
              {language === 'bm' || language === 'ms' ? 'Logout' : 'Logout'}
            </button>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              textAlign: 'center'
            }}>
              {t('footer.tagline')}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ 
        marginLeft: isMobile ? 0 : '260px', 
        width: isMobile ? '100%' : 'calc(100% - 260px)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Live Price Ticker - DISABLED (saves server resources for 500 concurrent users) */}
        {/* <LivePriceTicker symbols={['XAU/USD', 'EUR/USD', 'GBP/USD', 'USD/JPY']} /> */}
        
        {/* Header Bar - ALWAYS VISIBLE */}
        <header 
          data-testid="app-header"
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            maxWidth: '100vw',
            height: '56px',
            padding: '8px 16px',
            background: '#FFFFFF',
            borderBottom: '1px solid #F1F5F9',
            zIndex: 99,
            boxSizing: 'border-box'
          }}
        >
          {/* Logo */}
          <div 
            onClick={handleLogoClick}
            data-testid="logo-click-area-header"
            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
          >
            <img src="/mbe-logo.jpeg" alt="MBE" style={{ width: '34px', height: '34px', borderRadius: '10px', border: '2px solid #F5A623' }} />
            <div>
              <span style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>MBE Signal AI</span>
              <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '500' }}>Trade Engine</div>
            </div>
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />
          
          {/* Refresh Button */}
          <button
            onClick={() => {
              if ('caches' in window) caches.keys().then(n => n.forEach(name => caches.delete(name)));
              window.location.reload();
            }}
            data-testid="refresh-button"
            style={{
              background: '#F8FAFC',
              border: '1px solid #E2E8F0',
              color: '#64748B',
              padding: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px'
            }}
          >
            <RefreshCw size={16} />
          </button>
        </header>
        
        <main className="main-content" style={{ paddingBottom: isMobile ? '80px' : '20px', flex: 1, background: '#F8FAFC' }}>
          <div className="dashboard-container">

            {/* v10.2: NEW Home Screen (Decision-First) */}
            {activeTab === 'home' && (
              <div style={{ maxWidth: '480px', margin: '0 auto', padding: '0 4px' }}>
                <HomeScreen onNavigate={(tab) => handleTabChange(tab)} />
              </div>
            )}

            {/* Daily Checklist Tab */}
            {activeTab === 'checklist' && (
              <Suspense fallback={<TabFallback />}>
              <div className="dashboard-grid">
                <div style={{ gridColumn: '1 / -1' }}>
                  <DailyChecklist key={refreshKey} onComplete={() => {}} />
                </div>
              </div>
              </Suspense>
            )}


          {/* v10.2: Map new tab IDs to redesigned screens */}
          {activeTab === 'trade' && (
            <div style={{ maxWidth: '480px', margin: '0 auto', padding: '0 4px' }}>
              <TradeEngine onBack={() => handleTabChange('home')} />
            </div>
          )}

          {activeTab === 'learn' && (
            <div style={{ maxWidth: '480px', margin: '0 auto', padding: '0 4px' }}>
              <LearnHub onNavigate={(tab) => handleTabChange(tab)} />
            </div>
          )}

          {activeTab === 'performance' && (
            <div style={{ maxWidth: '480px', margin: '0 auto', padding: '0 4px' }}>
              <PerformanceDashboard />
            </div>
          )}

          {activeTab === 'profile' && (
            <div style={{ maxWidth: '480px', margin: '0 auto', padding: '0 4px' }}>
              <ProfileScreen />
            </div>
          )}

          {/* Premium Features Tab (with lazy loading) */}
          {activeTab === 'premium' && (
            <Suspense fallback={<LazyLoadFallback />}>
              <div className="dashboard-grid">
                <PremiumFeatures key={refreshKey} />
              </div>
            </Suspense>
          )}

          {/* Tools Tab - Combined Tools & Calculators */}
          {activeTab === 'tools' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              {/* Risk & Position Tools */}
              <RiskCalculator exchangeRate={exchangeRates.usd_to_sgd} />
              <TargetTracker exchangeRate={exchangeRates.usd_to_sgd} />
              <DrawdownAlert />
              
              {/* Calculators */}
              <PipValueCalculator exchangeRate={exchangeRates.usd_to_sgd} />
              <CompoundingCalculator exchangeRate={exchangeRates.usd_to_sgd} />
              <BreakEvenCalculator />
              
              {/* Market Tools */}
              <MarketSnapshot />
              <EconomicCalendar />
            </div>
            </Suspense>
          )}

          {/* Guide Tab */}
          {activeTab === 'guide' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid">
              <div style={{ gridColumn: '1 / -1' }}>
                <FeatureGuide onGuideComplete={handleGuideComplete} />
              </div>
            </div>
            </Suspense>
          )}


          {/* Fallback for invalid tabs - redirect to guide */}
          {!['home', 'trade', 'learn', 'performance', 'profile', 'guide', 'checklist', 'tools', 'premium'].includes(activeTab) && (
            <div className="dashboard-grid" key={refreshKey}>
              <div className="module-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Loading...</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
                  Redirecting...
                </p>
                <button 
                  onClick={() => setActiveTab('home')}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #F68B2D, #E07A1F)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Go to Guide
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      </div>
      {showGuideReminder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '420px',
            width: '100%',
            textAlign: 'center',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #F68B2D, #E07A1F)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 24px rgba(246, 139, 45, 0.4)'
            }}>
              <BookOpen size={32} color="#fff" />
            </div>
            
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              marginBottom: '12px'
            }}>
              {t('language.toggle') === 'Tukar Bahasa' 
                ? 'Guide Not Complete!' 
                : 'Guide Not Complete!'}
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              {t('language.toggle') === 'Tukar Bahasa'
                ? 'We highly recommend completing the guide and quiz first so you fully understand how to use MBE Signal AI.'
                : 'We highly recommend completing the guide and quiz first so you fully understand how to use MBE Signal AI.'}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={handleBackToGuide}
                style={{
                  padding: '14px 24px',
                  background: 'linear-gradient(135deg, #F68B2D, #E07A1F)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <BookOpen size={18} />
                Back to Guide
              </button>
              
              <button
                onClick={handleSkipGuide}
                style={{
                  padding: '14px 24px',
                  background: 'transparent',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-muted)'
                }}
              >
                Skip This Time
              </button>

              <button
                onClick={handleSkipGuidePermanently}
                style={{
                  padding: '10px 24px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: 'var(--text-muted)',
                  opacity: 0.7
                }}
              >
                {"✓ Don't Show Again"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bottom Navigation - Mobile Only (v10.2 Redesign) */}
      {isMobile && (
        <BottomNavNew 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
        />
      )}

      {/* PWA install prompt (non-blocking, dismissible for 7 days) */}
      {isAuthenticated && <InstallPrompt />}
    </div>
  );
}

function App() {
  const [showSystemGuideGlobal, setShowSystemGuideGlobal] = useState(false);
  
  // Global event listener for system guide
  useEffect(() => {
    const handleShowSystemGuide = () => setShowSystemGuideGlobal(true);
    window.addEventListener('showSystemGuide', handleShowSystemGuide);
    return () => window.removeEventListener('showSystemGuide', handleShowSystemGuide);
  }, []);
  
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <ToastProvider>
              <Routes>
                {/* Direct to Login - Skip Landing Page for better UX */}
                <Route path="/" element={<AuthPageWrapper />} />
                
                {/* Login/Register Page (alias) */}
                <Route path="/login" element={<AuthPageWrapper />} />
                
                {/* Password Reset Page */}
                <Route path="/reset-password" element={<AuthPageWrapper />} />
                
                {/* Protected Dashboard */}
                <Route path="/dashboard/*" element={<AppContent />} />
                
                {/* Redirect all unknown routes to login */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              {/* Global Live Support - Only on dashboard */}
              <SystemGuideModal 
                isOpen={showSystemGuideGlobal} 
                onClose={() => setShowSystemGuideGlobal(false)} 
              />
            </ToastProvider>
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

// Auth Page Wrapper - redirect if already logged in
function AuthPageWrapper() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LazyLoadFallback />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <AuthPage />;
}

export default App;
